// src/api/AxiosInstance.js
import axios from "axios";
import config from "../../CompanyData/DefaultData";

const axiosInstance = axios.create({
  baseURL: config.APIACTIVEURL,
});

// Add access token to each request
axiosInstance.interceptors.request.use((config) => {
  const tenantId = localStorage.getItem("tenant");
  if (tenantId) {
    config.headers["X-Tenant-ID"] = tenantId;
  }
  const accessToken = localStorage.getItem("accessToken");
  if (accessToken) {    
    config.headers["Authorization"] = `Bearer ${accessToken}`;
  }
  return config;
});

// Single-flight refresh: if several requests 401 at once, share one refresh
// call instead of each firing its own — avoids racing rotating refresh tokens
// where the second call invalidates the pair the first call just stored.
let refreshPromise = null;

const refreshAccessToken = () => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) throw new Error("No refresh token");

      const response = await axios.post(
        config.APIACTIVEURL + "auth/refresh",
        { refreshToken }
      );

      const { accessToken, refreshToken: newRefreshToken } = response.data;
      setTokens({ accessToken, refreshToken: newRefreshToken });
      return accessToken;
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

// Auto-refresh token on 401
axiosInstance.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalRequest = err.config;

    // Only retry once
    if (err.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const accessToken = await refreshAccessToken();

        // Update and retry original request
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshErr) {
        console.error("Token refresh failed:", refreshErr);

        // Refresh token itself is invalid/expired — this is a genuine auth
        // failure, so clear the session and send the user to login.
        clearTokens();
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }
    }

    return Promise.reject(err);
  }
);

// 🔐 Set both tokens in localStorage
export const setTokens = ({ accessToken, refreshToken }) => {
  if (accessToken) localStorage.setItem("accessToken", accessToken);
  if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
};

// 🔐 Remove tokens
export const clearTokens = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
};

export default axiosInstance;